import { DealStatusBadge } from '@/components/deal/status/deal-status-badge';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import getCallMeBy from '@/components/user/get-call-me-by.ts';
import { getQueryParamsForScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import type { AugmentedNotification } from '@/hooks/fetch-store-and-stitch/use-notifications';
import useGoToDeal from '@/hooks/use-go-to-deal';
import { useIsMobile } from '@/hooks/use-mobile.tsx';
import { getInformativeDealTitle } from '@/hooks/use-redirect-to-informative-deal-url';
import { cn } from '@/lib/utils';
import type { Notification } from '@/models/registry';
import { ArrowRightLeft, Dot, MessageSquareQuote } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';

const NotificationIcon = ({
  type,
  className,
}: {
  type: AugmentedNotification['type'];
  className: string;
}) => {
  switch (type) {
    case 'assignment':
      return <ArrowRightLeft className={cn(className)} />;
    case 'mention':
      return <MessageSquareQuote className={cn(className)} />;
  }
};

const InboxLineItemSkeleton = ({
  isActive = false,
}: {
  isActive?: boolean;
}) => {
  return (
    <div
      className={cn(
        'flex flex-row gap-2 p-2 border-b w-full scroll-m-18 dark:bg-dark animate-pulse',
        {
          'bg-gray-100 dark:is-active': isActive,
        },
      )}
    >
      <div className="relative">
        <div className="size-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
        <div className="bg-gray-300 dark:bg-gray-600 rounded-full absolute bottom-0 right-0 size-4" />
      </div>

      <div className="flex flex-col gap-1 w-3/4">
        <div className="flex flex-row items-center gap-2">
          <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
        <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>

      <div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  );
};

export const InboxLineItem = observer(
  ({
    notification,
    isActive,
    markAsRead,
  }: {
    notification: Notification;
    isActive: boolean;
    markAsRead: (notification: Notification) => void;
  }) => {
    const { isRead, deal, triggeredByUser } = notification;

    const displayDeal = deal;

    const { goToDeal } = useGoToDeal();
    const isMobile = useIsMobile();

    const isUnread = !isRead;

    const lineItemRef = useRef<HTMLDivElement>(null);

    // TODO (jimmy): wait for BE to send the comment id / reply id
    // DEV NOTE: we can not use the url fragment because the url fragment is broken with NextJS
    // See this issue: https://github.com/vercel/next.js/issues/77533
    const queryParams =
      notification.type === 'mention' && notification.metadata?.reply_id
        ? getQueryParamsForScrollAndFocus({
            uiType: 'reply',
            id: notification.metadata?.reply_id,
          })
        : notification.type === 'mention' && notification.metadata?.comment_id
          ? getQueryParamsForScrollAndFocus({
              uiType: 'comment',
              id: notification.metadata?.comment_id,
            })
          : undefined;

    if (!displayDeal) return <InboxLineItemSkeleton isActive={isActive} />;

    return (
      <div
        ref={lineItemRef}
        className={cn(
          'flex flex-row gap-2 p-2 border-b cursor-pointer w-full scroll-m-18 dark:bg-dark',
          {
            'bg-gray-100 dark:is-active': isActive,
          },
        )}
        key={displayDeal.id}
        onClick={() => {
          if (isUnread && markAsRead) {
            markAsRead(notification);
          }

          goToDeal({
            dealShortId: displayDeal.short_id,
            isForInbox: !isMobile,
            isFromMobileInbox: isMobile,
            queryParams,
          });
        }}
      >
        {triggeredByUser && (
          // TODO (jimmy): Flickering will since there's bug at the radix primative
          // https://github.com/radix-ui/primitives/issues/2044
          // We can either wait for this issue to get fixed or find a workaround
          <div className="relative">
            <PaddockAvatar
              src={triggeredByUser?.avatar}
              alt={triggeredByUser?.id}
              fallback={''}
              className="z-0 size-10"
              avatarClassName="size-10"
            />
            <NotificationIcon
              type={notification.type}
              className="bg-white rounded-full absolute bottom-0 right-0 size-4 text-black"
            />
          </div>
        )}
        <div className="flex flex-col gap-1  w-3/4">
          <div className="flex flex-row items-center gap-2">
            {isUnread && <Dot className="w-4 size-2 bg-primary rounded-full" />}
            <div
              className={cn('text-nowrap text-xs dark:text-white', {
                '': isUnread,
                'text-gray-500 ': !isUnread,
              })}
            >
              {displayDeal.short_id}
            </div>
            {displayDeal.primary_customer && displayDeal.vehicle_listing && (
              <div
                className={cn('text-nowrap text-xs truncate dark:text-white', {
                  '': isUnread,
                  'text-gray-500': !isUnread,
                })}
              >
                {getInformativeDealTitle({
                  primaryCustomerFullName:
                    displayDeal.primary_customer.full_name,
                  vehicle: displayDeal.vehicle_listing,
                  primarySalesRep: displayDeal.primary_sales_rep,
                  secondarySalesRep: displayDeal.secondary_sales_rep,
                })}
              </div>
            )}
          </div>
          <div
            className={cn('text-nowrap text-xs truncate dark:text-white', {
              'text-black': isUnread,
              'text-gray-500': !isUnread,
            })}
          >
            {triggeredByUser && getCallMeBy(triggeredByUser)}{' '}
            {notification.message}
          </div>
        </div>
        <div>
          <DealStatusBadge value={displayDeal.status} hideCopy />
        </div>
      </div>
    );
  },
);
